import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import JobDetailsModal from './JobDetailsModal';
import { jobService } from '../services/jobService';

export function SideNav({ onLockChange }) {
  const { logout, sideNavLocked, setSideNavLocked, user } = useAuth();
  const [isVisible, setIsVisible] = useState(sideNavLocked); // Initialize with locked state
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const navigate = useNavigate();

  // Sync isVisible with sideNavLocked when it changes from outside
  useEffect(() => {
    if (sideNavLocked) {
      setIsVisible(true);
    }
    // Notify parent component about current lock state
    if (onLockChange) {
      onLockChange(sideNavLocked);
    }
  }, [sideNavLocked, onLockChange]);

  const handleMouseEnter = () => {
    if (!sideNavLocked) {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!sideNavLocked) {
      setIsVisible(false);
    }
  };

  const handleLockToggle = () => {
    const newLockedState = !sideNavLocked;
    setSideNavLocked(newLockedState);
    if (newLockedState) {
      setIsVisible(true);
    }
    // Notify parent component about lock state change
    if (onLockChange) {
      onLockChange(newLockedState);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user's first name and first initial
  const getUserFirstName = () => {
    if (user?.firstName) {
      return user.firstName;
    }
    // Fallback to parsing name field if it exists (for backwards compatibility)
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    return 'User';
  };

  const getUserInitial = () => {
    const firstName = getUserFirstName();
    return firstName.charAt(0).toUpperCase();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowProfileDropdown(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setShowProfileDropdown(false);
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handlePipelineClick = () => {
    navigate('/job-tracker');
  };

  const handleTasksClick = () => {
    navigate('/tasks');
  };

  const handleFilesClick = () => {
    navigate('/documents');
  };

  const handleAddJobClick = () => {
    setJobModalOpen(true);
  };

  const handleJobModalClose = () => {
    setJobModalOpen(false);
  };

  const handleJobCreate = async (jobIdOrData, jobData = null) => {
    try {
      // If jobData is null, this is new job creation
      if (jobData === null) {
        // Create the job
        const newJob = await jobService.createJob(jobIdOrData);
        
        // Close the modal
        setJobModalOpen(false);
        
        console.log('Job created successfully from SideNav');
        return newJob; // Return the created job
      } else {
        // This would be an auto-save update (not expected from SideNav)
        throw new Error('Unexpected update call from SideNav');
      }
    } catch (error) {
      console.error('Error creating job from SideNav:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  // Burger Icon Component
  const BurgerIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  // Double Arrow Right (>>) Icon
  const LockIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7M15 5l7 7-7 7" />
    </svg>
  );

  // Double Arrow Left (<<) Icon  
  const UnlockIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7M9 19l-7-7 7-7" />
    </svg>
  );

  // Logout Icon
  const LogoutIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );

  // User Icon for Profile
  const UserIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  // Settings Icon
  const SettingsIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  // Home Icon
  const HomeIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );

  // Add Job Icon (Plus icon)
  const AddJobIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  // Pipeline Icon (Kanban board)
  const PipelineIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2z" />
    </svg>
  );

  // Tasks Icon (Task list)
  const TasksIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );

  // Files Icon (Folder icon)
  const FilesIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );

  return (
    <>
      {/* Burger Button - Always visible when sidenav is not locked */}
      {!sideNavLocked && (
        <div
          className="fixed top-4 left-4 z-50 cursor-pointer"
          onMouseEnter={handleMouseEnter}
        >
          <div className="bg-gray-800 hover:bg-gray-700 p-3 rounded-lg transition-colors duration-200">
            {isVisible ? (
              <div onClick={handleLockToggle} className="text-white">
                <LockIcon />
              </div>
            ) : (
              <div className="text-white">
                <BurgerIcon />
              </div>
            )}
          </div>
        </div>
      )}

      {/* SideNav Panel */}
      <div
        className={`fixed top-0 left-0 h-full bg-gray-900 border-r border-gray-700 z-40 transition-transform duration-300 ease-in-out flex flex-col ${
          isVisible || sideNavLocked ? 'transform translate-x-0' : 'transform -translate-x-full'
        }`}
        style={{ width: '256px' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col" style={{ 
          paddingTop: sideNavLocked ? '1rem' : '5rem' // When locked, start from top. When not locked, start below button
        }}>
          {/* User Profile Section */}
          <div className="px-4 mb-6">
            <div className="relative">
              <div 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-800 transition-colors duration-200 cursor-pointer group"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                {/* Profile Picture / Initial */}
                <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {user?.profilePicture ? (
                    <img 
                      src={user.profilePicture} 
                      alt="Profile" 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    getUserInitial()
                  )}
                </div>
                
                {/* User Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium text-sm truncate">
                    {getUserFirstName()}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {user?.email || 'user@example.com'}
                  </div>
                </div>

                {/* Unlock button when locked - Shows next to user name on hover */}
                {sideNavLocked && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent profile dropdown from toggling
                        handleLockToggle();
                      }}
                      className="text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded"
                      title="Unlock sidebar"
                    >
                      <UnlockIcon />
                    </button>
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <div 
                  className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-2 z-50"
                >
                  <button
                    onClick={handleProfileClick}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors duration-200 text-sm"
                  >
                    <UserIcon />
                    Profile
                  </button>
                  <button
                    onClick={handleSettingsClick}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 transition-colors duration-200 text-sm"
                  >
                    <SettingsIcon />
                    Settings
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Content */}
          <div className="flex-1 px-4">
            {/* First Separator */}
            <div className="border-t border-gray-700 mb-4"></div>
            
            {/* Home Navigation */}
            <button
              onClick={handleHomeClick}
              className="w-full flex items-center gap-3 px-3 py-3 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200 rounded-lg mb-4"
            >
              <HomeIcon />
              <span className="text-sm font-medium">Home</span>
            </button>

            {/* Second Separator */}
            <div className="border-t border-gray-700 mb-4"></div>

            {/* Add a Job Button */}
            <button
              onClick={handleAddJobClick}
              className="w-full flex items-center justify-center gap-2 px-3 py-3 text-gray-500 hover:text-white hover:bg-gray-800 border-2 border-dashed border-gray-600 hover:border-gray-500 rounded-lg transition-all duration-200"
            >
              <AddJobIcon />
              <span className="text-sm font-medium">Add a job</span>
            </button>

            {/* Pipeline Navigation */}
            <button
              onClick={handlePipelineClick}
              className="w-full flex items-center gap-3 px-3 py-3 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200 rounded-lg mt-4"
            >
              <PipelineIcon />
              <span className="text-sm font-medium">Pipeline</span>
            </button>

            {/* Tasks Navigation */}
            <button
              onClick={handleTasksClick}
              className="w-full flex items-center gap-3 px-3 py-3 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200 rounded-lg"
            >
              <TasksIcon />
              <span className="text-sm font-medium">Tasks</span>
            </button>

            {/* Files Navigation */}
            <button
              onClick={handleFilesClick}
              className="w-full flex items-center gap-3 px-3 py-3 text-gray-300 hover:text-white hover:bg-gray-800 transition-colors duration-200 rounded-lg"
            >
              <FilesIcon />
              <span className="text-sm font-medium">Docs</span>
            </button>

            {/* Third Separator - Below Files */}
            <div className="border-t border-gray-700 mt-4"></div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700">
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-4 py-3 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors duration-200"
            title="Logout"
          >
            <LogoutIcon />
            <span className="ml-3 text-sm">Logout</span>
          </button>

          {/* Elena Logo Footer */}
          <div className="p-4 flex justify-center">
            <img 
              src="/elena-logo 3.svg" 
              alt="Elena" 
              className="h-6 w-6 opacity-50"
            />
          </div>
        </div>
      </div>

      {/* Job Details Modal */}
      <JobDetailsModal
        open={jobModalOpen}
        onClose={handleJobModalClose}
        onSubmit={handleJobCreate}
        job={null}
        defaultStage="wishlist"
      />
    </>
  );
} 